import * as React from "react";
import { ExportReportPdfButton } from "@/components/ExportReportPdfButton";
import { useFinance } from "@/context/FinanceContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { parseISO, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Header } from "@/components/Header";
import { formatCurrency } from "@/utils/financeCalculations";
import { calculateHealthScore } from "@/components/FinancialHealthCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TrendingUp,
  TrendingDown,
  Target,
  Heart,
  Percent,
  Scale,
  ArrowUp,
  ArrowDown,
  Minus,
  AlertTriangle,
  ThumbsUp,
  CheckCircle2,
  AlertCircle,
  Lightbulb,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";

interface MonthData {
  month: number;
  year: number;
  label: string;
  income: number;
  expenses: number;
  profit: number;
  margin: number;
  breakEven: number;
  score: number;
}

function calcMonthData(
  transactions: ReturnType<typeof useFinance>["transactions"],
  month: number,
  year: number,
  costCenterFilter?: string
): MonthData {
  const monthTx = transactions.filter((t) => {
    if (!t.issueDate) return false;
    const d = parseISO(t.issueDate);
    const dateMatch = d.getMonth() === month && d.getFullYear() === year;
    const ccMatch = !costCenterFilter || costCenterFilter === "all" || t.costCenterId === costCenterFilter;
    return dateMatch && ccMatch;
  });

  const income = monthTx.filter((t) => t.type === "receita").reduce((s, t) => s + t.value, 0);
  const expenses = monthTx.filter((t) => t.type === "despesa").reduce((s, t) => s + t.value, 0);
  const profit = income - expenses;
  const margin = income > 0 ? (profit / income) * 100 : 0;
  const score = calculateHealthScore(margin);

  const label = format(new Date(year, month), "MMM/yy", { locale: ptBR });

  return { month, year, label, income, expenses, profit, margin, breakEven: expenses, score };
}

function getScoreColor(score: number) {
  if (score >= 8) return "text-emerald-600 dark:text-emerald-400";
  if (score >= 6) return "text-amber-600 dark:text-amber-400";
  if (score >= 4) return "text-orange-600 dark:text-orange-400";
  return "text-red-600 dark:text-red-400";
}

function getScoreBg(score: number) {
  if (score >= 8) return "bg-emerald-100 dark:bg-emerald-900/30";
  if (score >= 6) return "bg-amber-100 dark:bg-amber-900/30";
  if (score >= 4) return "bg-orange-100 dark:bg-orange-900/30";
  return "bg-red-100 dark:bg-red-900/30";
}

const currency = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

function SparkLine({ data, dataKey, color, height = 40 }: { data: MonthData[]; dataKey: keyof MonthData; color: string; height?: number }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <defs>
          <linearGradient id={`spark-${dataKey}-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey={dataKey as string} stroke={color} fill={`url(#spark-${dataKey}-${color})`} strokeWidth={2} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function VariationIndicator({ current, previous, suffix = "%", invertColor = false }: { current: number; previous: number; suffix?: string; invertColor?: boolean }) {
  if (previous === 0 && current === 0) {
    return <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"><Minus className="w-3 h-3" /> 0{suffix}</span>;
  }
  const pct = previous === 0 ? 100 : ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(pct) < 0.1) {
    return <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"><Minus className="w-3 h-3" /> 0{suffix}</span>;
  }
  const direction = pct > 0 ? "up" : "down";
  const isPositive = invertColor ? direction === "down" : direction === "up";

  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-semibold", isPositive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
      {direction === "up" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

const MONTHS_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export default function RelatorioRentabilidade() {
  const { transactions, costCenters } = useFinance();
  const [selectedCostCenter, setSelectedCostCenter] = React.useState<string>("all");

  // Build available month/year options from transactions
  const availableMonths = React.useMemo(() => {
    const s = new Set<string>();
    transactions.forEach((t) => {
      if (t.issueDate) {
        const d = parseISO(t.issueDate);
        s.add(`${d.getFullYear()}-${d.getMonth()}`);
      }
    });
    return Array.from(s)
      .map((k) => {
        const [y, m] = k.split("-").map(Number);
        return { year: y, month: m, key: k, label: `${MONTHS_SHORT[m]}/${y}` };
      })
      .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
  }, [transactions]);

  // Default: all months of current year up to current month
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(() => {
    const now = new Date();
    const keys = new Set<string>();
    for (let m = 0; m <= now.getMonth(); m++) {
      keys.add(`${now.getFullYear()}-${m}`);
    }
    return keys;
  });

  const toggleMonth = React.useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Year quick-select buttons
  const availableYears = React.useMemo(() => {
    const s = new Set<number>();
    availableMonths.forEach((m) => s.add(m.year));
    return Array.from(s).sort((a, b) => b - a);
  }, [availableMonths]);

  const selectYear = React.useCallback((year: number) => {
    const now = new Date();
    const maxMonth = year === now.getFullYear() ? now.getMonth() : 11;
    const keys = new Set<string>();
    for (let m = 0; m <= maxMonth; m++) {
      if (availableMonths.some((am) => am.year === year && am.month === m)) {
        keys.add(`${year}-${m}`);
      }
    }
    if (keys.size > 0) setSelectedKeys(keys);
  }, [availableMonths]);

  const monthlyData = React.useMemo(() => {
    const selected = availableMonths.filter((m) => selectedKeys.has(m.key));
    return selected.map((m) => calcMonthData(transactions, m.month, m.year, selectedCostCenter));
  }, [transactions, availableMonths, selectedKeys, selectedCostCenter]);

  const totals = React.useMemo(() => {
    const income = monthlyData.reduce((s, m) => s + m.income, 0);
    const expenses = monthlyData.reduce((s, m) => s + m.expenses, 0);
    const profit = income - expenses;
    const margin = income > 0 ? (profit / income) * 100 : 0;
    const score = calculateHealthScore(margin);
    return { income, expenses, profit, margin, breakEven: expenses, score };
  }, [monthlyData]);

  // Previous period for variation (same months, previous year)
  const prevTotals = React.useMemo(() => {
    const selected = availableMonths.filter((m) => selectedKeys.has(m.key));
    let income = 0, expenses = 0;
    selected.forEach((s) => {
      const d = calcMonthData(transactions, s.month, s.year - 1, selectedCostCenter);
      income += d.income;
      expenses += d.expenses;
    });
    const profit = income - expenses;
    const margin = income > 0 ? (profit / income) * 100 : 0;
    const score = calculateHealthScore(margin);
    return { income, expenses, profit, margin, breakEven: expenses, score };
  }, [transactions, availableMonths, selectedKeys, selectedCostCenter]);

  // Smart insights
  const insights = React.useMemo(() => {
    const msgs: { type: "success" | "warning" | "info"; text: string }[] = [];
    const withData = monthlyData.filter((m) => m.income > 0 || m.expenses > 0);
    if (withData.length < 2) return msgs;

    // Best/worst month
    const best = withData.reduce((a, b) => (a.profit > b.profit ? a : b));
    const worst = withData.reduce((a, b) => (a.profit < b.profit ? a : b));
    msgs.push({ type: "success", text: `Melhor mês: ${best.label} com lucro de ${currency.format(best.profit)}` });
    if (worst.profit < 0) {
      msgs.push({ type: "warning", text: `Pior mês: ${worst.label} com prejuízo de ${currency.format(worst.profit)}` });
    }

    // Trend (last 3 months)
    if (withData.length >= 3) {
      const last3 = withData.slice(-3);
      const profitTrend = last3[2].profit - last3[0].profit;
      if (profitTrend > 0) {
        const pct = last3[0].profit !== 0 ? ((profitTrend / Math.abs(last3[0].profit)) * 100).toFixed(1) : "∞";
        msgs.push({ type: "success", text: `Lucro cresceu ${pct}% nos últimos 3 meses` });
      } else if (profitTrend < 0) {
        msgs.push({ type: "warning", text: `Lucro caiu nos últimos 3 meses — revise despesas` });
      }
    }

    // Margin alert
    const lastMonth = withData[withData.length - 1];
    if (lastMonth.margin < 5) {
      msgs.push({ type: "warning", text: `Margem em ${lastMonth.label} está em ${lastMonth.margin.toFixed(1)}% — atenção!` });
    }

    // Score improvement
    if (totals.score >= 8) {
      msgs.push({ type: "success", text: "Saúde financeira excelente! Continue mantendo custos controlados." });
    } else if (totals.score < 4) {
      msgs.push({ type: "warning", text: "Saúde financeira crítica — revisar estratégia de receitas e despesas." });
    }

    return msgs;
  }, [monthlyData, totals]);

  const kpiCards = [
    { label: "Entradas", value: currency.format(totals.income), color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-900/20", border: "border-emerald-200 dark:border-emerald-800", icon: TrendingUp, sparkKey: "income" as const, sparkColor: "#059669", prev: prevTotals.income, cur: totals.income },
    { label: "Saídas", value: currency.format(totals.expenses), color: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-900/20", border: "border-red-200 dark:border-red-800", icon: TrendingDown, sparkKey: "expenses" as const, sparkColor: "#dc2626", prev: prevTotals.expenses, cur: totals.expenses, invertColor: true },
    { label: "Lucro", value: currency.format(totals.profit), color: totals.profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400", bg: totals.profit >= 0 ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-red-50 dark:bg-red-900/20", border: totals.profit >= 0 ? "border-emerald-200 dark:border-emerald-800" : "border-red-200 dark:border-red-800", icon: Scale, sparkKey: "profit" as const, sparkColor: totals.profit >= 0 ? "#059669" : "#dc2626", prev: prevTotals.profit, cur: totals.profit },
    { label: "Margem", value: `${totals.margin.toFixed(1)}%`, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-900/20", border: "border-blue-200 dark:border-blue-800", icon: Percent, sparkKey: "margin" as const, sparkColor: "#2563eb", prev: prevTotals.margin, cur: totals.margin },
    { label: "Ponto de Equilíbrio", value: currency.format(totals.breakEven), color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-900/20", border: "border-amber-200 dark:border-amber-800", icon: Target, sparkKey: "breakEven" as const, sparkColor: "#d97706", prev: prevTotals.breakEven, cur: totals.breakEven, invertColor: true },
    { label: "Nota", value: totals.score.toFixed(1), color: getScoreColor(totals.score), bg: getScoreBg(totals.score), border: "border-current/20", icon: Heart, sparkKey: "score" as const, sparkColor: "#8b5cf6", prev: prevTotals.score, cur: totals.score },
  ];

  const chartData = monthlyData.map((m) => ({
    name: m.label,
    Receita: m.income,
    Despesa: m.expenses,
    Lucro: m.profit,
    "Margem (%)": parseFloat(m.margin.toFixed(1)),
    Nota: parseFloat(m.score.toFixed(1)),
  }));

  const reportRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main ref={reportRef} className="page-bleed py-8 space-y-6">
        {/* Title */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold tracking-tight">Painel de Performance Financeira</h1>
            <p className="text-muted-foreground">Evolução mensal de rentabilidade e indicadores</p>
          </div>
          <ExportReportPdfButton
            targetRef={reportRef}
            title="Painel de Performance Financeira"
            filenameSlug="performance"
          />
        </div>


        {/* Filters */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex flex-col gap-4">
              {/* Year quick-select */}
              <div>
                <label className="text-sm font-medium mb-2 block">Ano</label>
                <div className="flex flex-wrap gap-2">
                  {availableYears.map((y) => (
                    <Badge
                      key={y}
                      variant="outline"
                      className={cn(
                        "cursor-pointer px-3 py-1 text-sm transition-colors",
                        [...selectedKeys].every((k) => k.startsWith(`${y}-`)) && [...selectedKeys].length > 0
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "hover:bg-muted"
                      )}
                      onClick={() => selectYear(y)}
                    >
                      {y}
                    </Badge>
                  ))}
                </div>
              </div>
              {/* Month chips */}
              <div>
                <label className="text-sm font-medium mb-2 block">Período</label>
                <div className="flex flex-wrap gap-2">
                  {availableMonths.map((m) => (
                    <Badge
                      key={m.key}
                      variant="outline"
                      className={cn(
                        "cursor-pointer px-3 py-1 text-sm transition-colors",
                        selectedKeys.has(m.key)
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "hover:bg-muted"
                      )}
                      onClick={() => toggleMonth(m.key)}
                    >
                      {m.label}
                    </Badge>
                  ))}
                </div>
              </div>
              {/* Cost center */}
              <div className="max-w-xs">
                <label className="text-sm font-medium mb-2 block">Centro de Custo</label>
                <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {costCenters.map((cc) => (
                      <SelectItem key={cc.id} value={cc.id}>{cc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards with sparklines */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {kpiCards.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className={cn("text-center p-4 rounded-lg border", kpi.bg, kpi.border)}>
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Icon className={cn("w-4 h-4", kpi.color)} />
                  <p className="text-sm text-muted-foreground">{kpi.label}</p>
                </div>
                <p className={cn("text-lg font-bold", kpi.color)}>{kpi.value}</p>
                <div className="mt-1">
                  <VariationIndicator current={kpi.cur} previous={kpi.prev} invertColor={kpi.invertColor} />
                  <span className="text-[10px] text-muted-foreground/70 block">vs ano anterior</span>
                </div>
                <div className="mt-2">
                  <SparkLine data={monthlyData} dataKey={kpi.sparkKey} color={kpi.sparkColor} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue vs Expense vs Profit */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Receita vs Despesa vs Lucro</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => currency.format(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="Receita" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Despesa" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Lucro" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Margin + Score */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Margem de Lucro e Nota de Saúde</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis yAxisId="left" className="text-xs" tickFormatter={(v: number) => `${v}%`} />
                  <YAxis yAxisId="right" orientation="right" className="text-xs" domain={[0, 10]} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="Margem (%)" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="Nota" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Breakdown Table - Transposed: months as columns, KPIs as rows */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Evolução Mensal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px] sticky left-0 bg-background z-10">Indicador</TableHead>
                    {monthlyData.map((m) => (
                      <TableHead key={m.label} className="text-center capitalize min-w-[100px]">{m.label}</TableHead>
                    ))}
                    <TableHead className="text-center font-bold min-w-[100px]">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Receita row */}
                  <TableRow>
                    <TableCell className="font-medium sticky left-0 bg-background z-10">Receita</TableCell>
                    {monthlyData.map((m) => (
                      <TableCell key={m.label} className="text-center text-emerald-600 dark:text-emerald-400">{currency.format(m.income)}</TableCell>
                    ))}
                    <TableCell className="text-center text-emerald-600 dark:text-emerald-400 font-bold">{currency.format(totals.income)}</TableCell>
                  </TableRow>
                  {/* Despesa row */}
                  <TableRow>
                    <TableCell className="font-medium sticky left-0 bg-background z-10">Despesa</TableCell>
                    {monthlyData.map((m) => (
                      <TableCell key={m.label} className="text-center text-red-600 dark:text-red-400">{currency.format(m.expenses)}</TableCell>
                    ))}
                    <TableCell className="text-center text-red-600 dark:text-red-400 font-bold">{currency.format(totals.expenses)}</TableCell>
                  </TableRow>
                  {/* Lucro row */}
                  <TableRow>
                    <TableCell className="font-medium sticky left-0 bg-background z-10">Lucro</TableCell>
                    {monthlyData.map((m) => (
                      <TableCell key={m.label} className={cn("text-center", m.profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>{currency.format(m.profit)}</TableCell>
                    ))}
                    <TableCell className={cn("text-center font-bold", totals.profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>{currency.format(totals.profit)}</TableCell>
                  </TableRow>
                  {/* Margem row */}
                  <TableRow>
                    <TableCell className="font-medium sticky left-0 bg-background z-10">Margem</TableCell>
                    {monthlyData.map((m) => (
                      <TableCell key={m.label} className="text-center text-blue-600 dark:text-blue-400">{m.margin.toFixed(1)}%</TableCell>
                    ))}
                    <TableCell className="text-center text-blue-600 dark:text-blue-400 font-bold">{totals.margin.toFixed(1)}%</TableCell>
                  </TableRow>
                  {/* Nota row */}
                  <TableRow>
                    <TableCell className="font-medium sticky left-0 bg-background z-10">Nota</TableCell>
                    {monthlyData.map((m) => (
                      <TableCell key={m.label} className={cn("text-center font-semibold", getScoreColor(m.score))}>{m.score.toFixed(1)}</TableCell>
                    ))}
                    <TableCell className={cn("text-center font-bold", getScoreColor(totals.score))}>{totals.score.toFixed(1)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Smart Insights */}
        {insights.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                Análise Inteligente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {insights.map((ins, i) => {
                  const Icon = ins.type === "success" ? CheckCircle2 : ins.type === "warning" ? AlertTriangle : Lightbulb;
                  const iconColor = ins.type === "success" ? "text-emerald-600" : ins.type === "warning" ? "text-amber-600" : "text-blue-600";
                  const borderColor = ins.type === "success" ? "border-l-emerald-500" : ins.type === "warning" ? "border-l-amber-500" : "border-l-blue-500";
                  return (
                    <div key={i} className={cn("flex items-start gap-3 p-3 rounded-lg border border-l-4", borderColor, "bg-card")}>
                      <Icon className={cn("w-5 h-5 mt-0.5 shrink-0", iconColor)} />
                      <p className="text-sm">{ins.text}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
